<template>
  <div class="advanced-search" v-show="!states.isBusy" data-cy="advanced-search-modal">
    <div class="advanced-search-project-management">
      <MenuBar without-border>
        <template #leftItems>
          <MSelect class="project-selection-dd" data-cy="project-selection-dd" v-model="vm.selectedSearch" :items="sortedSearches">
            <template #button-content>
              {{ vm.selectedSearch?.name }}
              <template v-if="states.isDirty">{{ `(${$t("unsaved")})` }}</template>
            </template>
            <MSelectItem
              v-for="item in sortedSearches"
              :key="item.advancedSearchId"
              :data-cy="'select-menu-entry-' + item.name"
              :active="vm.selectedSearch?.advancedSearchId == item.advancedSearchId"
              @click="selectSearch(item)">
              {{ item.name }}
            </MSelectItem>
          </MSelect>
          <b-btn class="btn-icon mono" data-cy="advance-search-new-btn" @click="createNewArticleSearch" v-tooltip="$t('add')">
            <Icon icon="Plus" />
          </b-btn>
          <b-btn class="btn-icon mono" data-cy="advanced-search-open-btn" @click="modals.open = true" v-tooltip.hover="$t('Open')">
            <Icon icon="Open" />
          </b-btn>
          <b-dropdown toggle-class="btn-icon mono" no-caret data-cy="advanced-search-more-ddn">
            <template #button-content> <Icon icon="Options"></Icon> </template>
            <b-dropdown-item
              link-class="icon-item"
              data-cy="advance-search-delete-btn"
              @click.prevent="deleteArticleSearch"
              :disabled="vm.articleSearches.length == 1 && vm.savedSearches.length == 0">
              <div class="dd-item-container">
                <span class="dd-item-icon"><Icon icon="Delete"></Icon></span>
                <span class="dd-item-text"> {{ $t("delete") }} </span>
              </div>
            </b-dropdown-item>
            <b-dropdown-item link-class="icon-item" @click.prevent="renameSearch" :disabled="!vm.articleSearches">
              <div class="dd-item-container">
                <span class="dd-item-icon"><Icon icon="Rename"></Icon></span>
                <span class="dd-item-text"> {{ $t("Rename") }} </span>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <template #rightItems>
          <div class="advanced-search-cta">
            <b-btn class="btn btn-with-icon" variant="primary" @click="vm.primaryAction?.clickHandler" data-cy="advance-search-primary-btn">
              <Icon v-if="vm.primaryAction?.icon" :icon="vm.primaryAction?.icon" />
              {{ vm.primaryAction?.text }}
            </b-btn>
            <b-dropdown toggle-class="btn-icon" variant="primary" data-cy="advance-search-primary-ddn" right no-caret>
              <template #button-content><Icon icon="ChevronBottom"></Icon></template>
              <div v-for="(entry, index) in vm.secondaryActions" :key="index">
                <b-dropdown-item v-if="entry" link-class="icon-item" :disabled="disabledMap[entry.type]" @click.prevent="entry.clickHandler">
                  <div class="dd-item-container">
                    <span class="dd-item-icon"><Icon v-if="entry?.icon" :icon="entry?.icon" /></span>
                    <span class="dd-item-text">{{ entry.text }}</span>
                  </div>
                </b-dropdown-item>
                <b-dropdown-divider v-else></b-dropdown-divider>
              </div>
            </b-dropdown>
          </div>
          <div v-tooltip="states.isDirty ? $t('AdvancedSearch.needsSave') : ''">
            <b-btn class="btn-with-icon mono" :disabled="states.isDirty" @click="runCsvExport" data-cy="advance-search-download-btn">
              <Icon icon="Download" />
              {{ $t("exportAsCSV") }}
            </b-btn>
          </div>
          <div v-tooltip="states.isDirty ? $t('AdvancedSearch.needsSave') : ''" v-if="isWorkManagerLicensed">
            <b-btn
              class="btn-with-icon mono"
              data-cy="advance-search-create-task-btn"
              @click.prevent="createTask"
              :disabled="states.isDirty || isBusyCreatingTask">
              <Icon icon="AddTask" v-show="!isBusyCreatingTask" />
              <Spinner size="16" v-show="isBusyCreatingTask" />
              {{ $t("contextMenu.createTask") }}
            </b-btn>
          </div>
        </template>
      </MenuBar>
      <OpenSearches v-model="modals.open" :searches="sortedSearches" @confirm="selectSearch" />
      <SearchRenameModal
        v-if="vm.draftSearch"
        v-model="vm.draftSearch.name"
        v-model:show="modals.rename"
        :selectedSearchId="vm.draftSearch.advancedSearchId"
        :title="searchRenameModalTitle"
        :ok-button-text="searchRenameModalOkButtonText"
        :max-characters-name="128"
        :saved-search-list="vm.savedSearches"
        @confirm="updateSearch"
        @cancel="cancelRename" />
      <div
        class="error-area"
        v-if="vm.errors.length > 0 || maxDepth > meta.config.advancedSearchMaxDepthAllowed"
        :class="{ show: vm.errors.length > 0 || maxDepth > meta.config.advancedSearchMaxDepthAllowed }">
        <Alert v-if="vm.errors.length === 1" class="m-0" show variant="danger">
          <div v-if="vm.errors.length">
            {{ getErrorLocalized(vm.errors[0]) }}
          </div>
          <div v-else-if="maxDepth > meta.config.advancedSearchMaxDepthAllowed">{{ $t("api.errors.AdvancedSearch.MaxGroupLevelExceeded") }}</div>
        </Alert>
        <Alert v-else class="w-100" with-icon show variant="danger">
          <div class="d-flex align-items-center">
            {{ $t("AdvancedSearch.Errors", { count: vm.errors.length }) }}
            <b-btn size="sm" variant="link" class="ml-1" v-b-modal.errors-overview> {{ $t("show") }} </b-btn>
          </div>
        </Alert>
        <b-modal
          header-close-content=""
          id="errors-overview"
          ok-only
          :ok-title="$t('Close')"
          :title="$t('AdvancedSearch.ErrorOverview')"
          body-class="warnings-overview-modal overflow-auto">
          <div v-for="entry in vm.errors" :key="_.isString(entry) ? entry : entry.error">
            <p class="mb-1">{{ getErrorLocalized(entry) }}</p>
          </div>
        </b-modal>
      </div>
    </div>
    <div class="tabs-wrapper" data-cy="advanced-search-tabs">
      <b-tabs v-model="states.currentTab">
        <b-tab :title="$t('editor')" />
        <b-tab :title="$t('results')" />
        <b-tab v-if="!props.hideExcelEdit" :disabled="states.isDirty" :title="$t('catalogNode.excelEdit')" />
      </b-tabs>

      <div class="advanced-search-editor-updated-on" v-if="vm.selectedSearch && vm.selectedSearch.updatedOn">
        <Icon icon="Clock" />
        {{ getDateTimeText(vm.selectedSearch.updatedOn) }}
      </div>
    </div>
    <div
      class="advanced-search-editor"
      data-cy="advanced-search-editor"
      :class="{ 'has-errors': vm.errors.length > 0 || maxDepth > meta.config.advancedSearchMaxDepthAllowed }"
      ref="advancedSearch"
      v-if="states.currentTab == Tabs.editor">
      <template v-if="vm.selectedSearch">
        <div class="advanced-search-editor-querybuilder-container" ref="querybuilder">
          <QueryBuilder v-model="vm.selectedSearch.searchJson" v-model:isBusy="states.isBusy" />
        </div>
      </template>
      <div v-else>{{ $t("needToSelectAdvSearch") }}</div>

      <div v-if="canActivateDebug" class="debug-mode-wrapper" :class="{ active: debugMode }">
        <div class="debug-mode-banner">
          <div class="debug-mode-title">DEBUG</div>
          <b-checkbox class="debug-mode-switch" switch v-model="debugMode">Show DataModel(DebugMode)</b-checkbox>
        </div>
        <textarea v-if="debugMode" class="debug-mode-textarea" :value="JSON.stringify(vm.selectedSearch?.searchJson, null, 4)" readonly> </textarea>
      </div>
    </div>
    <div class="advanced-search-results" v-if="states.currentTab == Tabs.results" data-cy="advanced-search-results">
      <div class="spinner-search-results" v-show="vm.busy">
        <Spinner />
      </div>
      <div :key="`sorted-${vm.sortedBy?.column}`">
        <div class="advanced-search-results-table" v-if="vm.resultList.length > 0">
          <div class="advanced-search-results-table-header">
            <div class="table-header-cell icon-cell"></div>
            <div class="table-header-cell status-cell" @click="setSortBy('stateSequence')">
              <div class="table-cell-text">{{ $t("AdvancedSearch.result.status") }}</div>
              <span class="table-header-cell-indicator" v-if="vm.sortedBy?.column == 'stateSequence'">
                <Icon :icon="vm.sortedBy?.desc ? 'ChevronBottom' : 'ChevronTop'" />
              </span>
            </div>
            <div class="table-header-cell article-no-cell" @click="setSortBy('articleNo')">
              <div class="table-cell-text">{{ $t("AdvancedSearch.result.articleNo") }}</div>
              <span class="table-header-cell-indicator" v-if="vm.sortedBy?.column == 'articleNo'">
                <Icon :icon="vm.sortedBy?.desc ? 'ChevronBottom' : 'ChevronTop'" />
              </span>
            </div>
            <div class="table-header-cell name-cell" @click="setSortBy('name')">
              <div class="table-cell-text">{{ $t("AdvancedSearch.result.name") }}</div>
              <span class="table-header-cell-indicator" v-if="vm.sortedBy?.column == 'name'">
                <Icon :icon="vm.sortedBy?.desc ? 'ChevronBottom' : 'ChevronTop'" />
              </span>
            </div>

            <div class="table-header-cell variation-group-cell" @click="setSortBy('variationGroupName')">
              <div class="table-cell-text">{{ $t("AdvancedSearch.result.variationGroupName") }}</div>
              <span class="table-header-cell-indicator" v-if="vm.sortedBy?.column == 'variationGroupName'">
                <Icon :icon="vm.sortedBy?.desc ? 'ChevronBottom' : 'ChevronTop'" />
              </span>
            </div>
          </div>
          <UseVirtualList ref="virtualList" :list="vm.resultList" height="calc(80vh - 190px)" :options="{ itemHeight: 30 }">
            <template #default="{ data: entry }">
              <div class="results-table-row" @click="navigateToContentEditor(entry?.articleId)">
                <div class="results-table-cell icon-cell">
                  <Icon icon="Article" />
                </div>
                <div class="results-table-cell status-cell">
                  <div class="entry-status-color" v-if="entry?.stateColor" :style="{ backgroundColor: entry.stateColor }"></div>
                </div>
                <div class="results-table-cell article-no-cell">
                  <span>{{ entry?.articleNo }}</span>
                </div>
                <div class="results-table-cell name-cell">
                  <span :class="{ fallback: entry?.name_IsFallback }"> {{ entry?.name }}</span>
                </div>
                <div class="results-table-cell variation-group-cell">
                  <span :class="{ fallback: entry?.variationGroupName_IsFallback }">{{ entry?.variationGroupName }}</span>
                </div>
              </div>
            </template>
          </UseVirtualList>
        </div>
        <EmptyState v-else>
          {{ vm.isSearchRunning ? $t("AdvancedSearch.emptyResults") : $t("AdvancedSearch.noExecutionResults") }}
        </EmptyState>
      </div>
    </div>
    <div class="advanced-search-excel-edit" v-if="states.currentTab == Tabs.excelEdit">
      <excel-edit-editor
        ref="excel-editor"
        isAdvancedSearchExcelEdit
        :advancedSearchId="vm.selectedSearch?.advancedSearchId"
        :advancedSearchCatalogId="props.catalogId ?? vm.baseCatalogId"
        :advancedSearchName="vm.selectedSearch?.name" />
    </div>
  </div>
  <div class="spinner-container" v-show="states.isBusy">
    <Spinner />
  </div>
  <div>
    <confirmationModal ref="userConfirmation"></confirmationModal>
  </div>
</template>
<script setup lang="ts">
import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import MSelectItem from "@/BasicComponentLibrary/MSelectItem.vue";
import OpenSearches from "@AdvancedSearch/components/OpenSearches.vue";
import SearchRenameModal from "@AdvancedSearch/views/SearchRenameModal.vue";
import { AttributeDataTypes } from "@AttributeManager/configurations/AttributeTypeDataTypes";
import excelEditEditor from "@ContentEditor/components/ExcelEdit/ExcelEditEditor.vue";
import Alert from "@common/components/Alert.vue";
import confirmationModal from "@common/components/ConfirmationModal.vue";
import Icon from "@common/components/Icon.vue";
import MenuBar from "@common/components/MenuBar/MenuBar.vue";
import Spinner from "@common/components/Spinner.vue";
import { useToast } from "@common/composable/toast";
import { useFiles } from "@common/composable/useFiles";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { useLicenses, useUser } from "@common/composable/useRootStore";
import {
  AdvArticleResult,
  AdvancedSearchAction,
  AdvancedSearchActionType,
  AdvancedSearchEntry,
  AdvancedSearchFetchedEntry,
  ErrorItem,
  NewAdvancedSearchEntry
} from "@medapro-types/AdvancedSearch/AdvancedSearch";
import { BaseQueryBuilderGroup, QueryBuilderData, QueryBuilderRule } from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { ApiErrorData, MutableRequired } from "@medapro-types/common/common";
import { RootState, User } from "@medapro-types/common/rootstate";
import advancedSearchApi from "@services/advancedSearch";
import endpoints from "@services/endpoints";
import userTasksApi from "@services/userTasks";
import { getDateTimeText } from "@util/dateTimeUtils";
import { BaseIds } from "@util/enums/base-ids";
import { generateNewName } from "@util/generateNewName";
import { Guid } from "@util/guid";
import { naturalSort } from "@util/naturalSort";
import { UseVirtualList } from "@vueuse/components";
import _ from "lodash";
import moment from "moment";
import { sha1 as hash } from "object-hash";
import { computed, nextTick, onMounted, provide, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useState } from "vuex-composition-helpers";
import QueryBuilder from "../components//QueryBuilder/QueryBuilder.vue";
import EmptyState from "@common/components/EmptyState.vue";

//#region General
//configureCompat({ COMPONENT_ASYNC: false });
const { t: $t, te: $te } = useI18n();
const router = useRouter();
const route = useRoute();
const emits = defineEmits(["hide", "update:advancedSearchId", "update:resultCount"]);
const props = defineProps<{ hideExcelEdit?: boolean; catalogId?: string; advancedSearchId?: string; executeSearchOnStart: boolean; resultCount?: number }>();
const { createToast } = useToast();
const userConfirmation = ref<typeof confirmationModal>();
const { triggerFileDownloadViaEndpoint } = useFiles();
//#endregion

//#region Store
const { user } = useUser();
const { meta } = useState<RootState>(["meta"]);
const { primaryLanguageCode } = useContentLanguages();

const { licenses } = useLicenses();
const isWorkManagerLicensed = computed(() => licenses.value?.WorkManager === "Licensed");

//#endregion

//#region Refs
const advancedSearch = ref<HTMLDivElement | null>(null);
const querybuilder = ref<HTMLDivElement | null>(null);
//#endregion

//#region ViewModel
type ViewModel = {
  baseCatalogId: string | undefined;
  selectedSearch: AdvancedSearchEntry | NewAdvancedSearchEntry | undefined;
  articleSearches: AdvancedSearchEntry[];
  resultList: Array<AdvArticleResult>;
  initialSearch: AdvancedSearchEntry | null;
  errors: ErrorItem[];
  draftSearch: AdvancedSearchEntry | null;
  savedSearches: AdvancedSearchEntry[];
  busy: boolean;
  isSearchRunning: boolean;
  sortedBy: { desc: boolean; column: keyof AdvArticleResult & string } | null;
  primaryAction: AdvancedSearchAction | null;
  secondaryActions: (AdvancedSearchAction | null)[];
};
const vm = reactive<ViewModel>({
  baseCatalogId: undefined,
  selectedSearch: undefined,
  articleSearches: [],
  resultList: [],
  initialSearch: null,
  errors: [],
  draftSearch: null,
  savedSearches: [],
  busy: false,
  isSearchRunning: false,
  sortedBy: null,
  primaryAction: null,
  secondaryActions: []
});
const isBusyCreatingTask = ref(false);

const modals = reactive({ rename: false, open: false });

enum Tabs {
  editor,
  results,
  excelEdit
}

enum SearchRenameModalModes {
  "rename",
  "saveAs",
  "create"
}

const states = reactive({ currentTab: Tabs.editor, isDirty: false, isBusy: false, searchRenameModalMode: SearchRenameModalModes.rename });

//#endregion

//#region Busy

provide("advancedSearchIsBusy", states.isBusy);

//#endregion

//#region Results

const setSortBy = async (columnName: keyof AdvArticleResult & string) => {
  if (vm.resultList.length > 0) {
    if (vm.sortedBy?.column == columnName) {
      vm.sortedBy.desc = !vm.sortedBy.desc;
    } else {
      vm.sortedBy = { column: columnName, desc: false };
    }

    sortResults();
  }
};

const sortResults = () => {
  vm.resultList = vm.sortedBy ? naturalSort(vm.resultList, vm.sortedBy.column, vm.sortedBy?.desc) : vm.resultList;
};

const canSaveSearch = computed<boolean>(() => {
  // Implmement check for user permissions etc. here
  return states.isDirty;
});

const saveSearch = async () => {
  if (canSaveSearch.value && states.isDirty) await saveArticleSearch();
};

const saveAsSearch = async () => {
  saveAsArticleSearch();
};

const saveAndRunSearch = async () => {
  await saveSearch();
  await runSearch();

  setSaveRunActionAsPrimary();
};

const runSearch = async () => {
  if (states.isDirty) return;

  vm.isSearchRunning = true;
  vm.resultList = [];
  states.currentTab = Tabs.results;
  let fetchedData: AdvArticleResult[] = [];

  if (vm.selectedSearch && vm.errors.length == 0) {
    try {
      states.isBusy = true;
      fetchedData = await advancedSearchApi.execute({
        advancedSearchId: vm.selectedSearch.advancedSearchId,
        catalogId: props.catalogId ?? vm.baseCatalogId ?? "00000000-0000-0000-0000-000000000000", // If catalogId is empty , search will execute on <Basiskatalog>
        language: primaryLanguageCode.value,
        returnArticleNo: true,
        returnLocalizedNames: true,
        returnState: true,
        orderDirection: false,
        orderByColumn: "name"
      });
    } catch (error: any) {
      if (error && error.isAxiosError) {
        if (error.response.status === 409) {
          let errorText = "";
          if (error.response.data.error == "UnknownSearchType") {
            errorText = $t("api.errors.AdvancedSearch." + error.response.data.error, [$t(`AdvancedSearch.SearchType.${error.response.data.args}`)]);
          }
          if (error.response.data.error == "UnknownOperatorForDataType") {
            const data = AttributeDataTypes.find((type) => type.key === error.response.data.args[0]);
            if (data) {
              error.response.data.args[0] = $t(data.name);
            }

            errorText = $t("api.errors.AdvancedSearch." + error.response.data.error, error.response.data.args);
          } else {
            errorText = $t("api.errors.AdvancedSearch." + error.response.data.error, error.response.data.args) ?? error.response.data.error;
          }

          createToast({
            text: errorText,
            title: $t("Error"),
            variant: "danger",
            titleIcon: "AdvancedSearch",
            textIllustration: "AdvancedSearch",
            statusTextIcon: "XCircleSolid"
          });
        }
      }
    } finally {
      states.isBusy = false;
      if (vm.busy) vm.busy = false;
    }
  }

  vm.resultList = fetchedData;
  vm.sortedBy = null;

  setRunActionAsPrimary();
};

const callToActions = reactive<Record<AdvancedSearchActionType, AdvancedSearchAction>>({
  run: {
    type: "run",
    text: $t("runSearch"),
    icon: "Play",
    clickHandler: runSearch
  },
  save: {
    type: "save",
    text: $t("Save"),
    icon: "Save",
    clickHandler: saveSearch
  },
  saveAs: {
    type: "saveAs",
    text: `${$t("saveAs")}...`,
    icon: "SaveAs",
    clickHandler: saveAsSearch
  },
  saveRun: {
    type: "saveRun",
    text: $t("saveAndRunSearch"),
    clickHandler: saveAndRunSearch
  }
});

const disabledMap = computed(() => ({
  run: states.isDirty,
  save: !canSaveSearch.value,
  saveRun: !canSaveSearch.value
}));

const navigateToContentEditor = (articleId: string) => {
  if (states.isDirty && !confirm($t("confirm.hasUnsavedData"))) return;
  states.isBusy = true;
  states.isDirty = false;

  nextTick(() => {
    router.push({ name: "article-editor", params: { id: articleId } });
    states.isBusy = false;
    emits("hide");
  });
};

//#endregion

//#region Exports

const runCsvExport = async () => {
  if (states.isDirty && !confirm($t("unsavedDataCantbeConsidered"))) return;
  if (vm.selectedSearch) {
    try {
      const response = await advancedSearchApi.exportToCsv(vm.selectedSearch.advancedSearchId, primaryLanguageCode.value);
      if (response != null) {
        triggerFileDownloadViaEndpoint(endpoints.advancedSearch.downloadCsv, {
          token: response,
          prefixName: encodeURIComponent(vm.selectedSearch.name)
        });
      }
    } catch (error: any) {
      if (error && error.isAxiosError) {
        if (error.response.status === 409) {
          let errorText = "";
          if (error.response.data.error == "UnknownSearchType") {
            errorText = $t("api.errors.AdvancedSearch." + error.response.data.error, [$t(`AdvancedSearch.SearchType.${error.response.data.args}`)]);
          }
          if (error.response.data.error == "UnknownOperatorForDataType") {
            const data = AttributeDataTypes.find((type) => type.key === error.response.data.args[0]);
            if (data) {
              error.response.data.args[0] = $t(data.name);
            }

            errorText = $t("api.errors.AdvancedSearch." + error.response.data.error, error.response.data.args);
          } else {
            errorText = $t("api.errors.AdvancedSearch." + error.response.data.error, error.response.data.args) ?? error.response.data.error;
          }

          createToast({
            text: errorText,
            title: $t("Error"),
            variant: "danger",
            titleIcon: "AdvancedSearch",
            textIllustration: "AdvancedSearch",
            statusTextIcon: "XCircleSolid"
          });
        }
      }
    } finally {
      states.isBusy = false;
      if (vm.busy) vm.busy = false;
    }
  }
};

//#endregion

//#region TypeGuard
function isNewAdvancedSearchEntry(e: AdvancedSearchEntry | NewAdvancedSearchEntry | undefined): e is NewAdvancedSearchEntry {
  return !!e && Object.hasOwn(e, "isNew");
}
const isQueryBuilderGroup = (e: unknown): e is BaseQueryBuilderGroup => {
  return !!e && Object.hasOwn(e, "groupId") && Object.hasOwn(e, "logic") && Object.hasOwn(e, "rules") && Object.hasOwn(e, "groups");
};
const isQueryBuilderRule = (e: unknown): e is QueryBuilderRule => {
  return !!e && Object.hasOwn(e, "ruleId") && Object.hasOwn(e, "searchType") && Object.hasOwn(e, "value");
};
//#endregion

//#region advSearches CRUD
const sortedSearches = computed(() => {
  return naturalSort(vm.articleSearches, (x) => x.name);
});

const selectSearch = (item: AdvancedSearchFetchedEntry | AdvancedSearchEntry, setDirty = false) => {
  if (item.advancedSearchId != vm.selectedSearch?.advancedSearchId) {
    if (states.isDirty && !confirm($t("confirm.hasUnsavedData"))) return;
    states.isBusy = true;
    vm.draftSearch = null;

    if (vm.selectedSearch && vm.initialSearch) {
      Object.assign(vm.selectedSearch, vm.initialSearch);
    }

    if (isNewAdvancedSearchEntry(vm.selectedSearch) && vm.selectedSearch.isNew && states.isDirty) {
      vm.articleSearches = _.filter(vm.articleSearches, (x) => !(isNewAdvancedSearchEntry(x) && x.isNew));
    }

    if (_.isString(item.searchJson)) {
      vm.initialSearch = _.cloneDeep(item);
      vm.selectedSearch = Object.assign(item, { searchJson: JSON.parse(item.searchJson) });
    } else {
      vm.initialSearch = _.cloneDeep(item);
      vm.selectedSearch = item;
    }

    vm.resultList = [];
    nextTick(() => {
      states.isDirty = setDirty;
      states.isBusy = false;
    });
  }
};

const loadAdvSearches = async () => {
  states.isBusy = true;

  try {
    vm.articleSearches = await advancedSearchApi.getAll();
    vm.savedSearches = _.cloneDeep(vm.articleSearches);
  } catch (error) {
    console.error("Error during fetching of Searches occured: ", error);
  } finally {
    states.isBusy = false;
  }
};

const createNewArticleSearch = () => {
  const currentUser = user.value;

  const newSearch: NewAdvancedSearchEntry = {
    advancedSearchId: Guid.new(),
    name: `${generateNewName($t("newSearch"), vm.savedSearches)}`,
    searchJson: {},
    createdBy: currentUser.username,
    updatedBy: currentUser.username,
    createdOn: moment().format("YYYY-MM-DD HH:mm:ss"),
    updatedOn: moment().format("YYYY-MM-DD HH:mm:ss"),
    isNew: true
  };
  states.currentTab = Tabs.editor;
  vm.draftSearch = newSearch;
  nextTick(() => {
    states.searchRenameModalMode = SearchRenameModalModes.create;
    showRenameModal();
  });
};

const showRenameModal = () => {
  if (!vm.draftSearch) return;
  modals.rename = true;
};

const updateSearch = async (/* newName: string */) => {
  if (states.searchRenameModalMode != SearchRenameModalModes.rename) {
    if (!vm.draftSearch) return;

    states.currentTab = Tabs.editor;
    vm.articleSearches.push(vm.draftSearch);
    vm.selectedSearch = vm.draftSearch;
    states.isDirty = true;
    if (states.searchRenameModalMode === SearchRenameModalModes.saveAs) {
      await saveArticleSearch();
    }
  } else {
    states.isDirty = true;
  }
  vm.draftSearch = null;
};

const cancelRename = () => {
  vm.draftSearch = null;
  if (vm.selectedSearch === undefined && states.searchRenameModalMode === SearchRenameModalModes.create) {
    emits("hide");
  }
};

const assignNewIdsinSearchJson = (jsonObj: QueryBuilderData | BaseQueryBuilderGroup | QueryBuilderRule) => {
  if (isQueryBuilderGroup(jsonObj)) {
    for (const group of jsonObj.groups) {
      assignNewIdsinSearchJson(group);
    }
    for (const rule of jsonObj.rules) {
      assignNewIdsinSearchJson(rule);
    }
  }
  if (isQueryBuilderGroup(jsonObj)) jsonObj.groupId = Guid.new();
  if (isQueryBuilderRule(jsonObj)) jsonObj.ruleId = Guid.new();
};

const deleteArticleSearch = async () => {
  if (vm.selectedSearch) {
    try {
      if (!isNewAdvancedSearchEntry(vm.selectedSearch)) {
        const response = await advancedSearchApi.canDelete(vm.selectedSearch.advancedSearchId, primaryLanguageCode.value);
        if (response != null) {
          if (response.canDelete) {
            if (confirm($t("confirm.searchDelete", { selectedSearch: vm.selectedSearch.name })))
              await advancedSearchApi.delete(vm.selectedSearch.advancedSearchId);
            else return;
          } else {
            const names = response.catalogNames || [];
            const visibleNames = names.slice(0, 3).join("\n");
            const catalogNames = `${visibleNames} ${names.length > 3 ? $t("confirm.andOthers", { count: names.length - 3 }) : ""}`;

            const confirmation = await getDeleteConfirmation(catalogNames);
            if (confirmation) await advancedSearchApi.delete(vm.selectedSearch.advancedSearchId);
            else return;
          }
        }
      } else {
        if (!confirm($t("confirm.searchDelete", { selectedSearch: vm.selectedSearch.name }))) return;
      }
      _.remove(vm.articleSearches, (x) => x == vm.selectedSearch);
      vm.savedSearches = _.cloneDeep(vm.articleSearches);
      states.isDirty = false;
      selectFirstOrCreate();
    } catch (error) {
      console.error("Error deleting search:", error);
    }
  }
};

const getDeleteConfirmation = async (catalogNames: string) => {
  let confirmation = false;
  try {
    await userConfirmation.value?.getConfirmation($t("confirm.deleteArticleFilter"), $t("confirm.canNotDeleteSearch", { catalogNames: catalogNames }), false);
    confirmation = true;
  } catch (e) {
    console.log("Error during confirmation:", e);
  }
  return confirmation;
};

const saveArticleSearch = async () => {
  if (vm.selectedSearch) {
    try {
      const searchJSON = removeUnnecessaryProperties(_.cloneDeep(vm.selectedSearch.searchJson));
      await advancedSearchApi.save({
        advancedSearchId: vm.selectedSearch.advancedSearchId,
        name: vm.selectedSearch.name,
        searchJson: searchJSON
      });
      if (isNewAdvancedSearchEntry(vm.selectedSearch)) delete vm.selectedSearch.isNew;
      vm.savedSearches = _.cloneDeep(vm.articleSearches);
      states.isDirty = false;
      vm.selectedSearch.updatedOn = moment().toISOString();
      vm.initialSearch = _.cloneDeep(vm.selectedSearch);
      vm.errors = [];
    } catch (error: any) {
      handleErrors(error);
    }
  }
};

const removeUnnecessaryProperties = (obj: QueryBuilderData) => {
  return _.cloneDeepWith(obj, (value) => {
    if (isQueryBuilderRule(value)) {
      delete value.dataUIType;
      if (!value.attributeId) delete value.attributeId;
      if (!value.dataLanguage) delete value.dataLanguage;
    }
  });
};

const renameSearch = () => {
  if (vm.selectedSearch) {
    vm.draftSearch = vm.selectedSearch;
    states.searchRenameModalMode = SearchRenameModalModes.rename;
    showRenameModal();
  }
};

const saveAsArticleSearch = () => {
  const currentUser: User = { ...user.value } as MutableRequired<User>;

  const searchJsonCopy = _.cloneDeep(vm.selectedSearch?.searchJson) ?? {};

  assignNewIdsinSearchJson(searchJsonCopy);

  vm.draftSearch = vm.selectedSearch
    ? Object.assign(_.cloneDeep(vm.selectedSearch), {
        advancedSearchId: Guid.new(),
        searchJson: searchJsonCopy,
        createdBy: currentUser,
        updatedBy: currentUser,
        createdOn: moment().format("YYYY-MM-DD HH:mm:ss"),
        updatedOn: moment().format("YYYY-MM-DD HH:mm:ss")
      })
    : null;

  if (states.isDirty) {
    if (!confirm($t("confirm.hasUnsavedData"))) {
      vm.draftSearch = null;
      return;
    }

    /*  if (isNewAdvancedSearchEntry(vm.selectedSearch)) {
      _.remove(vm.articleSearches, (x) => x === vm.selectedSearch);
    } else if (vm.tempName != "" && vm.selectedSearch) {
      vm.selectedSearch.name = vm.tempName;
    } */
  }

  states.searchRenameModalMode = SearchRenameModalModes.saveAs;
  showRenameModal();
};

async function createTask() {
  if (vm.selectedSearch) {
    try {
      isBusyCreatingTask.value = true;
      const newTaskId = await userTasksApi.createUserTaskFromAdvancedSearch(vm.selectedSearch.advancedSearchId);
      if (newTaskId) {
        const query = !route.fullPath.includes("work-manager") ? { back: route.fullPath } : {};
        nextTick(() => {
          router.push({ name: "task-item", params: { id: newTaskId }, query });
          emits("hide");
        });
      }
    } catch (error: any) {
      let errorMessage = $t("ErrorMessage.TaskCreationFailed");
      if (error && error.isAxiosError) {
        if (error.response.status === 409) {
          errorMessage = $te(`api.errors.AdvancedSearch.${error?.response?.data.error}`)
            ? $t(`api.errors.AdvancedSearch.${error?.response?.data.error}`)
            : error?.response?.data.error;
        }
      }
      createToast({
        text: errorMessage,
        title: $t("Error"),
        variant: "danger",
        titleIcon: "AdvancedSearch",
        textIllustration: "AdvancedSearch",
        statusTextIcon: "XCircleSolid"
      });
    } finally {
      isBusyCreatingTask.value = false;
    }
  }
}

const selectFirstOrCreate = async () => {
  if (sortedSearches.value.length) {
    let selectedElement: AdvancedSearchEntry | AdvancedSearchFetchedEntry | undefined;
    if (props.advancedSearchId) {
      selectedElement = sortedSearches.value.find((search) => search.advancedSearchId === props.advancedSearchId);
    } else {
      selectedElement = _.first(sortedSearches.value);
    }

    if (selectedElement) selectSearch(selectedElement);
  } else {
    createNewArticleSearch();
  }
};

watch(
  () => vm.selectedSearch,
  (nv) => {
    if (nv) {
      emits("update:advancedSearchId", nv.advancedSearchId);
    }
  },
  { deep: true }
);

watch(
  () => vm.selectedSearch?.name,
  () => {
    vm.isSearchRunning = false;
  }
);

watch(
  () => states.isDirty,
  (nv, ov) => {
    if (vm.primaryAction?.type === "saveRun") {
      return;
    }
    if (nv === true && ov === false) {
      setSaveActionAsPrimary();
      return;
    }
    setRunActionAsPrimary();
  }
);

watch(
  () => vm.selectedSearch?.searchJson,
  (nv) => {
    if (nv && vm.initialSearch && !states.isDirty) {
      const newJsonHash = hash(nv);
      const oldJsonHash = hash(vm.initialSearch.searchJson!);
      if (newJsonHash != oldJsonHash) states.isDirty = true;
    }
  },
  { deep: true }
);

watch(
  () => vm.resultList,
  () => {
    if (states.currentTab == Tabs.results) {
      emits("update:resultCount", vm.resultList?.length);
    } else {
      emits("update:resultCount", null);
    }
  }
);

watch(
  () => states.currentTab,
  () => {
    if (states.currentTab == Tabs.results) {
      emits("update:resultCount", vm.resultList?.length);
    } else {
      emits("update:resultCount", null);
    }
  }
);
//#endregion

//#region SearchRenameModal
const searchRenameModalTitle = computed(() => {
  switch (states.searchRenameModalMode) {
    case SearchRenameModalModes.create:
      return $t("SearchRenameModal.create");

    case SearchRenameModalModes.rename:
      return $t("SearchRenameModal.rename");

    case SearchRenameModalModes.saveAs:
      return $t("SearchRenameModal.saveAs");
  }
  return "";
});
const searchRenameModalOkButtonText = computed(() => {
  switch (states.searchRenameModalMode) {
    case SearchRenameModalModes.rename:
      return $t("Rename");
    case SearchRenameModalModes.saveAs:
      return $t("Save");
    case SearchRenameModalModes.create:
      return $t("Add");
  }

  return "";
});
//#endregion

//#region LifeCycle

onMounted(async () => {
  vm.baseCatalogId = BaseIds.BaseCatalog;
  await loadAdvSearches();
  selectFirstOrCreate();

  if (props.executeSearchOnStart) {
    vm.busy = true;
    saveAndRunSearch();
  }

  setRunActionAsPrimary();
});

//#endregion

//#region Exposed

defineExpose({ states });

//#endregion

//#region Debug-Functions
const debugMode = ref(false);

const canActivateDebug = computed(() => {
  return false;
});
//#endregion

//#region Depth - Calculation
const getMaxDepth = (groups: BaseQueryBuilderGroup[], depth = 1): number => {
  if (!groups || groups.length === 0) return depth;
  return Math.max(...groups.map((group: BaseQueryBuilderGroup) => getMaxDepth(group.groups, depth + 1)));
};

const maxDepth = computed(() => getMaxDepth(isQueryBuilderGroup(vm.selectedSearch?.searchJson) ? vm.selectedSearch.searchJson.groups : []));
//#endregion

//#region Validation
function handleErrors(error: any) {
  vm.errors = [];
  const responseData = error.response?.data;
  if (error && error.isAxiosError && error.response.status === 400) {
    if (responseData?.Name[0]) {
      createToast({
        text: $t("api.errors.AdvancedSearch." + responseData?.Name[0]),
        title: $t("Error"),
        variant: "danger",
        titleIcon: "AdvancedSearch",
        textIllustration: "AdvancedSearch",
        statusTextIcon: "XCircleSolid"
      });
    }
  }
  if (Object.hasOwn(responseData, "errors")) {
    responseData.errors.forEach((entry: ApiErrorData) => {
      if (entry?.error) {
        const errorItem: ErrorItem = { ruleId: Guid.isGuid(entry.value) ? entry.value : null, error: entry?.error, message: entry?.message };
        vm.errors.push(errorItem);
      }
    });
  }
  if (Object.hasOwn(responseData, "error")) {
    vm.errors.push(responseData?.error);
  }
}

function getErrorLocalized(error: ErrorItem): string {
  if (_.isString(error)) return $t(`api.errors.AdvancedSearch.${error}`);
  if (!Guid.isGuid(error.ruleId)) return $t(`api.errors.AdvancedSearch.${error.error}`);

  const rule = findRuleById(vm.selectedSearch?.searchJson, error.ruleId);
  if (!isQueryBuilderRule(rule)) {
    vm.errors = [];
    return "";
  }
  if (rule.ruleId) rule.ruleId;
  const errorMessage = $t(`api.errors.AdvancedSearch.${error.error}`);
  const searchTypeLocalized = $t(`AdvancedSearch.SearchType.${rule.searchType}`);
  return `${errorMessage} (${searchTypeLocalized})`;
}

function findRuleById(group: BaseQueryBuilderGroup | unknown, id: Guid): QueryBuilderRule | null {
  if (!isQueryBuilderGroup(group)) return null;

  const rule = group.rules.find((rule) => rule.ruleId === id);
  if (rule) return rule;

  for (const subGroup of group.groups) {
    const foundInSubGroup = findRuleById(subGroup, id);
    if (foundInSubGroup) return foundInSubGroup;
  }

  return null;
}

function setRunActionAsPrimary() {
  vm.primaryAction = callToActions.run;
  vm.secondaryActions = [callToActions.save, callToActions.saveAs, null, callToActions.saveRun];
}

function setSaveActionAsPrimary() {
  vm.primaryAction = callToActions.save;
  vm.secondaryActions = [callToActions.saveAs, null, callToActions.saveRun];
}

function setSaveRunActionAsPrimary() {
  vm.primaryAction = callToActions.saveRun;
  vm.secondaryActions = [callToActions.run, null, callToActions.save, callToActions.saveAs];
}
//#endregion;
</script>
